import { useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { MLProject, MLProjectScriptPart } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';
import webVTT from '@/utils/webVtt';

import Card, { ActionButton } from './Card';
import LanguagesSelection from './LanguagesSelection';
import ScriptField from './ScriptField';
import ScriptTranslationsPreview from './ScriptTranslationsPreview';
import TimestampField from './TimestampField';

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 800px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  margin-bottom: 24px;
  &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  }
`;

export const Editor = styled.div<{ $withBorderBottom?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  max-height: 840px;
  overflow-y: auto;
  position: relative;
  ${scrollBarStyles};

  ${({ $withBorderBottom = false }) =>
    !$withBorderBottom &&
    css`
      border-radius: 4px 4px 0 0;
      border-bottom: 0;
    `}
`;

export const Row = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const Column = styled.div`
  padding: 12px 24px;
`;

export const TimestampsColumn = styled(Column)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 230px;
`;

export const ScriptColumn = styled(Column)`
  flex: 1;
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

export const ColumnTitle = styled(Text).attrs({
  $color: 'gray',
  $type: 'button',
})`
  margin-bottom: 8px;
  padding-top: 12px;
`;

export const EditorFooterActions = styled.div`
  padding-bottom: 12px;
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const Note = styled(Text)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  svg {
    margin-right: 4px;
  }
`;

export const ScriptFieldWrap = styled.div`
  display: flex;
  align-items: flex-start;
`;

export type FormDataT = {
  script: MLProjectScriptPart[];
};

interface Props {
  project: MLProject;
}

const ScriptsCard: FC<Props> = ({ project }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);

  const {
    control,
    handleSubmit,
    setFocus,
    setError,
    reset,
    clearErrors,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<FormDataT>({
    defaultValues: {
      script: project.script ?? [
        {
          timestamp: '',
          text: '',
        },
      ],
    },
  });

  const { fields, insert, replace, remove } = useFieldArray({
    control,
    name: 'script',
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      const toastId = toasts.create('loading');

      try {
        await api.post(
          `/v1/distribution/ml-projects/${project.id}/script`,
          data,
        );

        queryClient.setQueryData<MLProject>(
          [
            'ml_projects',
            {
              id: project.id.toString(),
            },
          ],
          (oldData) => ({ ...oldData!, script: data.script }),
        );

        reset(data);

        toasts.success(toastId, 'Saved');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
        toasts.close(toastId);
      }
    })(e);
  };

  const onClear = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      replace([
        {
          timestamp: '',
          text: '',
        },
      ]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const subtitlesSrc = useMemo(() => {
    if (!project.script?.length) {
      return null;
    }

    // const arr = project.script.map((row) => {
    //   const timestamp = row.timestamp
    //     .split(' - ')
    //     .map((part, index) => part + (!index ? '.001' : '.000'))
    //     .join(' --> ');

    //   return {
    //     ...row,
    //     timestamp,
    //   };
    // });

    const vtt = webVTT.fromArray(project.script);

    return webVTT.toDataSrc(vtt);
  }, [project.script]);

  const isSaveButtonDisabled =
    !isDirty || isSubmitting || !isValid || !!project.translations?.length;

  const existingLanguages = useMemo(() => {
    return project.translations!.map((t) => t.language);
  }, [project.translations]);

  return (
    <Card
      storageKey={`ml_project_${project.id}_scripts_visibility`}
      defaultIsOpened={project.status === MLProjectStatus.DRAFT}
      title="Original script translation with timestamps"
      description="Paste the script text, add timestamps, and use ChatGPT for translation and structuring the information"
      actions={
        <ActionButton onClick={onSubmit} disabled={isSaveButtonDisabled}>
          Save
        </ActionButton>
      }
      footer={
        project.translations!.length ? (
          <ScriptTranslationsPreview
            translations={project.translations!}
            script={project.script}
          />
        ) : undefined
      }
    >
      {!!project.video && (
        <Video controls ref={videoRef}>
          <source src={project.video.versions.original} type="video/mp4" />
          {!!subtitlesSrc && (
            <track
              label="English"
              kind="subtitles"
              srcLang="en"
              src={subtitlesSrc}
              default
            />
          )}
          Your browser does not support the video tag.
        </Video>
      )}
      <Editor $withBorderBottom>
        <Row>
          <TimestampsColumn>
            <ColumnTitle>Timestamps</ColumnTitle>
          </TimestampsColumn>
          <ScriptColumn>
            <ColumnTitle>Script</ColumnTitle>
          </ScriptColumn>
        </Row>
        {fields.map(({ id }, index) => (
          <Row key={id}>
            <TimestampsColumn>
              <TimestampField
                index={index}
                control={control}
                videoRef={videoRef}
              />
            </TimestampsColumn>
            <ScriptColumn>
              <ScriptFieldWrap>
                <ScriptField
                  index={index}
                  control={control}
                  replace={replace}
                  insert={insert}
                  setFocus={setFocus}
                  remove={remove}
                  fieldsCount={fields.length}
                />
              </ScriptFieldWrap>
              {index + 1 === fields.length && (
                <>
                  <Note $type="smallNormal" $color="gray">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.6">
                        <circle cx="6" cy="6" r="6" fill="currentColor" />
                        <path
                          d="M5.4375 4.30147V3.375H6.5625V4.30147H5.4375ZM5.4375 8.625V4.91912H6.5625V8.625H5.4375Z"
                          fill="white"
                        />
                      </g>
                    </svg>
                    Hit enter to start new paragraph with timestamp
                  </Note>
                  <EditorFooterActions>
                    <ActionButton
                      onClick={onSubmit}
                      disabled={isSaveButtonDisabled}
                    >
                      Save
                    </ActionButton>
                    <ActionButton
                      onClick={onClear}
                      $secondary
                      disabled={!!project.translations?.length}
                    >
                      Clear All
                    </ActionButton>
                  </EditorFooterActions>
                </>
              )}
            </ScriptColumn>
          </Row>
        ))}
      </Editor>
      {!!project.script?.length && (
        <LanguagesSelection
          projectStatus={project.status}
          projectId={project.id}
          existingLanguages={existingLanguages}
        />
      )}
    </Card>
  );
};

export default ScriptsCard;
